import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Utils } from '@pbt/pbt-ui-components'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import FeatureToggle from '~/constants/featureToggle'
import { InventoryCategoryName } from '~/constants/inventoryCategory'
import {
  getFeatureToggle,
  getInventoryCategory,
  getProcedureCategory,
} from '~/store/reducers/constants'
import type { InvoiceLineItem } from '~/types'
import {
  convertInvoiceLineItemToSoapLog,
  convertRetailOrderLineItemToSoapLog,
  getInventoryItemCategoryId,
} from '~/utils/orderUtils'

const PROCEDURE_CATEGORY = {
  VACCINATION: 'Vaccinations',
} as const

export const useIsItemPrescribable = (
  item: InvoiceLineItem | RetailOrderLineItem,
  isRetailOrderLineItem: boolean,
  isBundle: boolean,
): boolean => {
  const InventoryCategory = useSelector(getInventoryCategory)
  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )

  return useMemo(() => {
    if (isBundle) return false

    const convertedItem = isRetailOrderLineItem
      ? convertRetailOrderLineItemToSoapLog(item as RetailOrderLineItem)
      : convertInvoiceLineItemToSoapLog(item as InvoiceLineItem)

    const drugCategoryId = Utils.findConstantIdByName(
      InventoryCategoryName.DRUG,
      InventoryCategory,
    )
    const vetDietCategoryId = Utils.findConstantIdByName(
      InventoryCategoryName.VET_DIET,
      InventoryCategory,
    )
    const itemCategoryId = getInventoryItemCategoryId(
      convertedItem,
      Boolean(isFoodCatalogEnabled),
    )
    const isDrugOrVetDiet =
      itemCategoryId === drugCategoryId || itemCategoryId === vetDietCategoryId

    const foodCategoryId = Utils.findConstantIdByName(
      InventoryCategoryName.FOOD,
      InventoryCategory,
    )
    const isFood = itemCategoryId === foodCategoryId
    const requiresPrescription = Boolean(
      convertedItem.inventory?.requiresPrescription,
    )

    return isDrugOrVetDiet || (isFood && requiresPrescription)
  }, [
    item,
    isRetailOrderLineItem,
    isBundle,
    InventoryCategory,
    isFoodCatalogEnabled,
  ])
}

export const useIsItemVaccine = (
  item: InvoiceLineItem | RetailOrderLineItem,
  isRetailOrderLineItem: boolean,
  isBundle: boolean,
): boolean => {
  const ProcedureCategory = useSelector(getProcedureCategory)

  return useMemo(() => {
    if (isBundle || isRetailOrderLineItem) return false

    const { procedureCategoryId } = item as InvoiceLineItem
    if (!procedureCategoryId) return false

    const vaccinationCategoryId = Utils.findConstantIdByName(
      PROCEDURE_CATEGORY.VACCINATION,
      ProcedureCategory,
    )

    return procedureCategoryId === vaccinationCategoryId
  }, [item, isRetailOrderLineItem, isBundle, ProcedureCategory])
}
